import React, { useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment";

import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { selectHasPermissionToManageAutoscaling } from "Reducers/environment";
import { getAutoscalingConfigurationThunk } from "Reducers/environment/service/service.slice";
import { projectCapabilitySelector } from "Reducers/project/capabilities";
import ActivityLogModal from "src/organization/pages/project/environment/metrics/components/ActivityLogModal/ActivityLogModal";
import ViewPortOnly from "src/organization/pages/project/environment/metrics/components/ViewPortOnly";
import { MetricsDeploymentService } from "src/organization/pages/project/environment/metrics/containers/Service/type";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import usePollActivities from "src/organization/pages/project/environment/metrics/hooks/usePollActivities";
import { useStubService } from "src/organization/pages/project/environment/metrics/resources/Grid/util";
import { defaultPollTime } from "src/organization/pages/project/environment/metrics/resources/settings";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { parseTimeDuration } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/parseDimensions";
import usePollgroup, {
  PollgroupContext
} from "src/organization/pages/project/environment/metrics/utils/polling/usePollgroup";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import ServiceUnits from "./ServiceUnits";

import type { Activity } from "@packages/client";

const router = { name: "router" };

const ServiceMetrics = ({ serviceName }) => {
  const [maxZoom, setMaxZoom] = useState(false);
  const dispatch = useAppDispatch();
  const initialTimeframe = useInitialTimeframe();
  const [timeframe, updateTimeFrame] = useState(initialTimeframe);
  const [enableDeployment, setEnableDeployment] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [activities, setActivities] = useState<Activity[]>([]);
  const { organizationId, projectId, environmentId } = useDecodedParams();

  const canManageAutoscaling = useSelectorWithUrlParams(
    selectHasPermissionToManageAutoscaling
  );

  const pollInterval = parseTimeDuration(
    initialTimeframe.poll || defaultPollTime[timeframe.id || "fallback"]
  );

  const capabilites = useAppSelector(state =>
    projectCapabilitySelector(state, { projectId, organizationId })
  );
  const isAutoscalingEnabled = capabilites?.autoscaling?.enabled;

  usePollActivities({
    enabled: enableDeployment || isAutoscalingEnabled,
    timeframe,
    onChange: setActivities,
    interval: pollInterval.seconds
  });

  const { pollgroup } = usePollgroup({
    timeframe,
    interval: pollInterval.seconds
  });

  const setTimeFrame = useCallback((timeframe: TimeFrameType) => {
    updateTimeFrame(timeframe);
    setMaxZoom(false);
  }, []);

  const onBrush = useCallback(
    (from: Date | string, to: Date | string) => {
      if (maxZoom) {
        updateTimeFrame(timeframe);
        return;
      }
      const selectedRange = moment(to).diff(moment(from), "minutes");
      if (selectedRange < 3) {
        const newEndpoint = moment(from).add(3, "minutes").format();
        setMaxZoom(true);
        updateTimeFrame({ from, to: newEndpoint });
      } else {
        updateTimeFrame({ from, to });
      }
    },
    [maxZoom, setMaxZoom, timeframe]
  );

  const { all } = useStubService();

  const service = useMemo(
    () =>
      serviceName === "router"
        ? router
        : all?.find(({ name }) => name === serviceName),
    [all, serviceName]
  );

  useEffect(() => {
    dispatch(getAutoscalingConfigurationThunk({ projectId, environmentId }));
  }, [dispatch, projectId, environmentId]);

  const prop = {
    setLastUpdated,
    setTimeFrame,
    selectedTimeframe: timeframe,
    onBrush,
    isAutoscalingEnabled,
    enableDeployment,
    setEnableDeployment,
    activities: activities || [],
    maxZoom,
    canManageAutoscaling,
    interval: pollInterval,
    lastUpdated
  };

  return (
    <PollgroupContext.Provider value={{ pollgroup }}>
      <ActivityLogModal />
      <ViewPortOnly>
        <ServiceUnits {...prop} service={service as MetricsDeploymentService} />
      </ViewPortOnly>
    </PollgroupContext.Provider>
  );
};

export default ServiceMetrics;
